import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CDB_FORM_META, cdbFormFillPayload, cdbFormUrl } from "../../../utils/cdbForms";
import { downloadCdbFormPdf } from "../../../utils/cdbFormPdf";
import { printCdbForms } from "../../../utils/cdbFormPrint";
import "../css/KycSectionDocuments.css";
import "../css/KycCdbFormsPanel.css";

// Editable, pre-filled CDB forms shown in the app's document-preview modal.
// One badge per form in the header; switching fades the current form out and the next one in.
// Each form stays loaded once opened, so entries made in it are kept when switching back.
// Print and PDF cover all forms (in badge order) in one print job / one PDF file.
export default function KycCdbFormModal({ forms = [], formId, onClose }) {
  const frames = useRef({});
  const [activeId, setActiveId] = useState(formId);
  const [mounted, setMounted] = useState(() => new Set());
  const [loaded, setLoaded] = useState(() => new Set());
  const [busy, setBusy] = useState("");          // "pdf" | "print" while all forms are prepared
  const [pdfError, setPdfError] = useState("");
  const loadedRef = useRef(new Set());
  const loadWaiters = useRef({});

  useEffect(() => {
    setActiveId(formId);
    if (!formId) {
      setMounted(new Set());
      setLoaded(new Set());
      loadedRef.current = new Set();
      loadWaiters.current = {};
    }
  }, [formId]);

  useEffect(() => {
    if (activeId) setMounted((prev) => (prev.has(activeId) ? prev : new Set(prev).add(activeId)));
  }, [activeId]);

  useEffect(() => {
    if (!formId) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [formId, onClose]);

  if (!formId) return null;

  const letterCount = forms.reduce((acc, f) => ({ ...acc, [f.form]: (acc[f.form] || 0) + 1 }), {});
  const badgeLabel = (form) => (letterCount[form.form] > 1 && form.subjectName ? `Form ${form.form} · ${form.subjectName}` : `Form ${form.form}`);
  const active = forms.find((f) => f.id === activeId);

  // Mounts every form (hidden ones included) and resolves with their documents once filled.
  async function allFormDocuments() {
    setMounted(new Set(forms.map((f) => f.id)));
    await Promise.all(forms.map((f) => (loadedRef.current.has(f.id)
      ? null
      : new Promise((resolve) => { loadWaiters.current[f.id] = resolve; }))));
    const docs = forms.map((f) => frames.current[f.id]?.contentDocument).filter(Boolean);
    await Promise.all(docs.map((d) => d.fonts?.ready));
    return docs;
  }

  const fileBase = () => {
    const slug = (text) => String(text || "").replace(/[^\p{L}\p{N}]+/gu, "-").replace(/^-|-$/g, "");
    if (forms.length > 1) return `CDB-Forms${slug(forms[0]?.contractingPartner) ? `-${slug(forms[0].contractingPartner)}` : ""}`;
    const subject = slug(active?.subjectName || active?.contractingPartner);
    return `CDB-Form-${active?.form}${subject ? `-${subject}` : ""}`;
  };

  async function handlePdf() {
    setBusy("pdf");
    setPdfError("");
    try {
      await downloadCdbFormPdf(await allFormDocuments(), `${fileBase()}.pdf`);
    } catch (e) {
      console.error("CDB form PDF failed:", e);
      setPdfError("The PDF could not be generated. Use the print button to save it as PDF.");
    } finally {
      setBusy("");
    }
  }

  async function handlePrint() {
    setBusy("print");
    try {
      await printCdbForms(await allFormDocuments(), fileBase());
    } finally {
      setBusy("");
    }
  }

  function handleLoad(form) {
    frames.current[form.id]?.contentWindow?.CDBForm?.fill(cdbFormFillPayload(form, forms));
    loadedRef.current.add(form.id);
    loadWaiters.current[form.id]?.();
    delete loadWaiters.current[form.id];
    setLoaded((prev) => new Set(prev).add(form.id));
  }

  return createPortal(
    <div className="docModalOverlay docModalOverlayPreview kycCdbFormModal" onMouseDown={onClose}>
      <div className="docModalStack" onMouseDown={(e) => e.stopPropagation()}>
        <div className="docModalShell" role="dialog" aria-modal="true" aria-label={active ? `Form ${active.form}` : "CDB form"}>
          <div className="docModalHeader">
            <div className="kycCdbFormBadges" role="tablist" aria-label="CDB forms">
              {forms.map((form) => (
                <button key={form.id} type="button" role="tab" aria-selected={form.id === activeId}
                  className={`kycCdbFormBadge${form.id === activeId ? " isActive" : ""}`}
                  title={`Form ${form.form} · ${CDB_FORM_META[form.form]?.title || ""}${form.subjectName ? ` — ${form.subjectName}` : ""}`}
                  onClick={() => setActiveId(form.id)}>
                  {badgeLabel(form)}
                </button>
              ))}
            </div>
            <div className="kycCdbFormModalActions">
              <button type="button" className="docModalCloseBtn" disabled={!loaded.has(activeId) || !!busy}
                onClick={handlePdf} aria-label="Download PDF" aria-busy={busy === "pdf"}
                title={forms.length > 1 ? `Download PDF (all ${forms.length} forms)` : "Download PDF"}>
                {busy === "pdf"
                  ? <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                  : <i className="bi bi-filetype-pdf" aria-hidden="true" />}
              </button>
              <button type="button" className="docModalCloseBtn" disabled={!loaded.has(activeId) || !!busy}
                onClick={handlePrint} aria-label="Print or save as PDF" aria-busy={busy === "print"}
                title={forms.length > 1 ? `Print / Save as PDF (all ${forms.length} forms)` : "Print / Save as PDF"}>
                {busy === "print"
                  ? <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                  : <i className="bi bi-printer" aria-hidden="true" />}
              </button>
              <button type="button" className="docModalCloseBtn" onClick={onClose} aria-label="Close form">
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            </div>
          </div>
          {pdfError ? <div className="alert alert-warning mb-0 rounded-0 py-2" role="alert">{pdfError}</div> : null}
          <div className="docModalBody kycCdbFormModalBody">
            {forms.filter((form) => mounted.has(form.id)).map((form) => (
              <iframe key={form.id} ref={(el) => { frames.current[form.id] = el; }}
                title={`Form ${form.form}`} src={cdbFormUrl(form)}
                className={`kycCdbFormFrame${form.id === activeId ? " isActive" : ""}`}
                aria-hidden={form.id !== activeId} tabIndex={form.id === activeId ? 0 : -1}
                onLoad={() => handleLoad(form)} />
            ))}
            {!loaded.has(activeId) ? (
              <div className="docPreviewLoader">
                <div>
                  <div className="appDotsLoader" aria-hidden="true" />
                  <div className="appTextLoader" data-text="Preparing form..." aria-label="Preparing form..." />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
